import { CONSENT } from "./config";
import { debugLog } from "./debug";
import { isBrowser } from "./env";
import { metrics } from "./metrics";
import type { ConsentCategory, ConsentState, ConsentStatus } from "./types";

// Stable hash of policy version + copy signature → version string
function policyVersionHash(): string {
  return `${CONSENT.policy_version}-${CONSENT.policy_copy_signature}`;
}

const DEFAULTS: ConsentState = {
  status: "unknown",
  categories: { essential: true, analytics: true, marketing: true, replay: true },
  decided_at: null,
  version: policyVersionHash(),
};

type Listener = (s: ConsentState) => void;
const listeners = new Set<Listener>();

let state: ConsentState = { ...DEFAULTS };

function emit(): void {
  for (const l of listeners) {
    try {
      l(state);
    } catch {
      /* never throw from consent */
    }
  }
}

function load(): void {
  if (!isBrowser()) return;
  try {
    const raw = localStorage.getItem(CONSENT.storage_key);
    if (!raw) return;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== policyVersionHash()) {
      // policy changed → treat as unknown, force re-prompt
      state = { ...DEFAULTS };
      return;
    }
    state = { ...DEFAULTS, ...parsed };
  } catch {
    state = { ...DEFAULTS };
  }
}

function persist(): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(CONSENT.storage_key, JSON.stringify(state));
  } catch {
    /* noop */
  }
}

export const consent = {
  init(): void {
    load();
    emit();
    debugLog("consent:init", state);
  },
  snapshot(): ConsentState {
    return { ...state, categories: { ...state.categories } };
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  allows(cat: ConsentCategory): boolean {
    return state.categories[cat] === true;
  },
  isDecided(): boolean {
    return state.status !== "unknown";
  },
  acceptAll(): void {
    state = {
      status: "accepted",
      categories: { essential: true, analytics: true, marketing: true, replay: true },
      decided_at: new Date().toISOString(),
      version: policyVersionHash(),
    };
    persist();
    metrics.consentDecision("accepts");
    emit();
    debugLog("consent:accept_all", state);
  },
  rejectNonEssential(): void {
    state = {
      status: "rejected",
      categories: { essential: true, analytics: false, marketing: false, replay: false },
      decided_at: new Date().toISOString(),
      version: policyVersionHash(),
    };
    persist();
    metrics.consentDecision("rejects");
    emit();
    debugLog("consent:reject_non_essential", state);
  },
  setPartial(cats: Partial<Record<ConsentCategory, boolean>>): void {
    state = {
      status: "partial",
      categories: { ...state.categories, ...cats, essential: true },
      decided_at: new Date().toISOString(),
      version: policyVersionHash(),
    };
    persist();
    metrics.consentDecision("partials");
    emit();
    debugLog("consent:partial", state);
  },
  reset(): void {
    state = { ...DEFAULTS };
    if (isBrowser()) {
      try {
        localStorage.removeItem(CONSENT.storage_key);
      } catch {
        /* noop */
      }
    }
    emit();
  },
  decisionKind(): "accept_all" | "reject_non_essential" | "partial" | null {
    const s: ConsentStatus = state.status;
    if (s === "accepted") return "accept_all";
    if (s === "rejected") return "reject_non_essential";
    if (s === "partial") return "partial";
    return null;
  },
};
